import React, { useState, useEffect } from "react";
import {
  Trophy,
  Users,
  Heart,
  Search,
  MessageSquare,
  Share2,
  Activity,
  Loader2,
  AlertCircle,
  Crown,
  Baby,
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const getAvatarColor = (name) => {
  if (!name) return "bg-gray-500";
  const colors = [
    "bg-red-500",
    "bg-orange-500",
    "bg-amber-500",
    "bg-yellow-500",
    "bg-lime-500",
    "bg-green-500",
    "bg-emerald-500",
    "bg-teal-500",
    "bg-cyan-500",
    "bg-sky-500",
    "bg-blue-500",
    "bg-indigo-500",
    "bg-violet-500",
    "bg-purple-500",
    "bg-fuchsia-500",
    "bg-pink-500",
    "bg-rose-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const PowerUsersPanel = ({ getAccessToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = getAccessToken();
        const response = await fetch(`${API_URL}/v2/admin/analytics/users/power-users?limit=10`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error("Error fetching power users:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessToken]);

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading power users...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
        <div className="flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>Error loading power users</span>
        </div>
        <p className="text-red-300 text-sm mt-2">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { powerUsers, stats } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Power Users</h3>
            <p className="text-sm text-gray-400">Most engaged families</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-yellow-400">{stats.totalPowerUsers}</div>
          <div className="text-xs text-gray-400">power users</div>
        </div>
      </div>

      {/* Power Users List */}
      <div className="space-y-3">
        {powerUsers.map((user, index) => (
          <div
            key={user.id}
            className="flex items-center gap-4 p-4 bg-gray-800/30 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
          >
            {/* Rank */}
            <div className="flex-shrink-0">
              {index === 0 ? (
                <div className="w-8 h-8 rounded-full bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center">
                  <Crown className="w-4 h-4 text-yellow-400" />
                </div>
              ) : index === 1 ? (
                <div className="w-8 h-8 rounded-full bg-gray-400/20 border border-gray-400/50 flex items-center justify-center text-gray-300 font-bold text-sm">
                  2
                </div>
              ) : index === 2 ? (
                <div className="w-8 h-8 rounded-full bg-amber-700/20 border border-amber-700/50 flex items-center justify-center text-amber-600 font-bold text-sm">
                  3
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-700/50 flex items-center justify-center text-gray-500 text-sm">
                  {index + 1}
                </div>
              )}
            </div>

            {/* Avatar */}
            <div className="flex-shrink-0">
              {user.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt={user.name || "User"}
                  className="w-12 h-12 rounded-full border-2 border-gray-600"
                />
              ) : (
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-medium ${getAvatarColor(
                    user.name || user.email
                  )}`}
                >
                  {getInitials(user.name)}
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium truncate">
                  {user.name || "Anonymous User"}
                </span>
                {user.household && (
                  <span className="flex items-center gap-1 text-xs text-gray-400 bg-gray-700/50 px-2 py-0.5 rounded-full">
                    <Baby className="w-3 h-3" />
                    {user.household.childCount}
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-400 truncate">{user.email}</div>
              <div className="flex items-center gap-3 mt-2">
                {user.metrics.savedActivities > 0 && (
                  <div className="flex items-center gap-1 text-xs text-pink-400">
                    <Heart className="w-3 h-3" />
                    {user.metrics.savedActivities}
                  </div>
                )}
                {user.metrics.programViews > 0 && (
                  <div className="flex items-center gap-1 text-xs text-blue-400">
                    <Search className="w-3 h-3" />
                    {user.metrics.programViews}
                  </div>
                )}
                {user.metrics.activityRequests > 0 && (
                  <div className="flex items-center gap-1 text-xs text-green-400">
                    <Activity className="w-3 h-3" />
                    {user.metrics.activityRequests}
                  </div>
                )}
                {user.metrics.chatMessages > 0 && (
                  <div className="flex items-center gap-1 text-xs text-purple-400">
                    <MessageSquare className="w-3 h-3" />
                    {user.metrics.chatMessages}
                  </div>
                )}
                {user.metrics.shares > 0 && (
                  <div className="flex items-center gap-1 text-xs text-indigo-400">
                    <Share2 className="w-3 h-3" />
                    {user.metrics.shares}
                  </div>
                )}
              </div>
            </div>

            {/* Engagement Score */}
            <div className="text-right">
              <div className="text-xl font-bold text-yellow-400">
                {user.engagementScore.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">engagement</div>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-400 pt-4 border-t border-gray-700">
        <div className="flex items-center gap-1">
          <Heart className="w-3 h-3 text-pink-400" />
          Saves
        </div>
        <div className="flex items-center gap-1">
          <Search className="w-3 h-3 text-blue-400" />
          Views
        </div>
        <div className="flex items-center gap-1">
          <Activity className="w-3 h-3 text-green-400" />
          Requests
        </div>
        <div className="flex items-center gap-1">
          <MessageSquare className="w-3 h-3 text-purple-400" />
          Messages
        </div>
        <div className="flex items-center gap-1">
          <Share2 className="w-3 h-3 text-indigo-400" />
          Shares
        </div>
      </div>

      {/* Stats Summary */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-indigo-400" />
            <div>
              <div className="text-white font-medium">User Engagement Overview</div>
              <div className="text-sm text-gray-400">
                {stats.usersWithSaves} users have saved activities
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-white">
              {stats.totalPowerUsers > 0
                ? Math.round((stats.totalPowerUsers / stats.usersWithSaves) * 100)
                : 0}%
            </div>
            <div className="text-xs text-gray-400">power user rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PowerUsersPanel;
